// AddressBlock — the ONE address control: city · district · street · number · pin,
// each able to fill the others.
// ============================================================================
// Owner, 2026-08-08: "use city district street building or street number", and
// "user selects pin and district is selected, or writes in address and pin is
// selected and picked everything itself".
//
// Two directions, both resolved through ONE definition of "which district is this
// point in" — `district_codes_for_point`, wrapped as GET /api/core/geo/district:
//
//   type an address → street suggestion → the street's own coordinates → district
//   drop/drag a pin →                     the pin's coordinates        → district
//
// ⚠️ The street's district is NOT read from `street.district`. That column is free
// text and measurably unusable as an identity: ი.ჭავჭავაძის გამზ. reports
// "ვაკე-საბურთალო / ვაკე" (two raions joined), and every Kutaisi street reports
// "ქუთაისის უბნები" — "Kutaisi's neighbourhoods", a bucket, not a place. Matching
// those strings to `district.code` would mean a second definition of district
// membership. Coordinates already have one.
//
// ── WHAT THIS NEVER DOES ────────────────────────────────────────────────────
// It never silently overwrites a district a human chose. `property` already models
// this disagreement — `declared_district_code` (what a person said) vs
// `district_code_geo` (what the pin says) vs `district_status='disputed'` — and the
// dispute queue (B274) is built on it. So: auto-fill happens only while the human
// has not overridden, an override is remembered, and a conflict is SHOWN rather
// than resolved. A form that quietly picked a winner would erase the evidence.
//
// ⚠️ RENDERED IN BOTH BUNDLES. React + districtModel + DistrictPicker only — no
// db, no i18n, no design primitives. Network access arrives as INJECTED FUNCTIONS
// (`resolveStreet`, `districtForPoint`) because the Desk calls through coreApi and
// the public fill form through its own token-scoped fetch; the component must not
// know which. Every string is a prop, same contract as DistrictPicker.
// ============================================================================

import { useEffect, useRef, useState } from 'react'
import DistrictPicker from './DistrictPicker.jsx'
import { DEFAULT_CITY, citiesOf, cityValue, districtLabel } from '../lib/districtModel.js'
import { houseNumberFromQuery } from '../lib/houseNumber.js'

const BOX = 'w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground'

// ⚠️ The browser must keep its own suggestions out of this control.
// Owner, 2026-08-09: "don't autofill." Chrome treats anything that looks like an
// address field as a candidate for its saved addresses, so its list opens over OURS —
// offering strings from some other site that mean nothing to the gazetteer, on top of
// suggestions that resolve to real coordinates. `autoComplete="off"` alone is widely
// ignored for address-shaped fields; a name the browser cannot classify is what
// actually stops it. autoCorrect/autoCapitalize/spellCheck go too: this is Georgian
// text, and a phone keyboard "correcting" a street name is the same defect wearing
// a different hat.
const NO_AUTOFILL = {
  autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: false,
  name: 'sntv-addr-nofill', 'data-lpignore': 'true', 'data-1p-ignore': 'true',
}

export default function AddressBlock({
  value = {},                    // { address, street, street_number, entrance_apt, district_code, city, lat, lng }
  onChange,                      // (patch) => void — always a PATCH, never the whole object
  districts = [],
  lang = 'ka',
  resolveStreet = null,          // async (q, city) => { best, candidates } | null
  districtForPoint = null,       // async (lat, lng) => { district_code, city_name_ka } | null
  addressForPoint = null,        // async (lat, lng) => { road, house_number, street, precision } | null
  houseForAddress = null,        // async (street, number, lat, lng) => { found, lat, lng } | null
  onUnresolved = null,           // (bool) => void — true while the pin's reading is unanswered
  // The host's own refusal, aimed at the ONE card that can answer it. A class to
  // pulse (the host owns the animation; this component owns no CSS) and the message
  // to print inside it.
  attention = '',
  blockedMessage = null,
  renderMap = null,              // ({ lat, lng, onPick, layout }) => node — host supplies its own map
  // 'search' — one input, the map confirms it. 'map' — the map IS the step and the
  // pin names the building. The two arms of 0516's alternation; everything below the
  // capture (city, district, disagreement) is deliberately identical in both.
  layout = 'search',
  // Map BESIDE the fields from lg up (the property modal's redesign, 2026-09-16). Off by default, so the
  // owner's fill-in form keeps its stacked flow; below lg it is always stacked.
  mapBeside = false,
  labels = {},
}) {
  const L = {
    street: 'Street', street_ph: 'Start typing the street…', number: 'No.',
    district: 'District', city: 'City', searching: 'Searching…', no_street: 'No match — your text is kept as typed',
    auto: 'filled from the address', auto_pin: 'filled from the pin',
    conflict: 'The pin points at a different district than the one chosen.',
    keep_mine: 'Keep mine', use_pin: 'Use the pin’s',
    at_pin: 'At this pin', use_it: 'Use it',
    pin_says: 'The pin is on a different building', pin_keep: 'Keep what I wrote',
    pin_decide: 'Choose one to continue',
    map_search_ph: 'Street or landmark', or_map: 'Can’t find it? Just mark it on the map',
    other_number: 'Different number? Fix it', unnamed: 'This spot — we could not name it',
    other: 'Other',
    change: 'Change',
    entrance_add: '+ Entrance / apartment', entrance_label: 'Entrance / apartment',
    entrance_note: 'For your agent only — never published',
    or: 'or', mark_on_map: 'Mark on the map', my_location: 'My location',
    not_listed_hint: 'Street not in our list? Type it anyway — it still reaches your agent.',
    ...labels,
  }

  const [q, setQ] = useState(value.street || '')
  const [hits, setHits] = useState(null)      // null = idle, [] = searched and empty
  const [busy, setBusy] = useState(false)
  const [autoFrom, setAutoFrom] = useState(null)   // 'address' | 'pin' | null
  const [conflict, setConflict] = useState(null)   // { code } the pin says, while the human's differs
  // A district the human picked BY HAND. Once set, nothing auto-fills over it —
  // this is the whole "never silently overwrite a declaration" rule, held in one flag.
  const overridden = useRef(false)
  const seq = useRef(0)
  const pinSeq = useRef(0)
  const districtSeq = useRef(0)   // #19 — district answers are ordered independently of pins
  // The district the ADDRESS/PIN resolved to, remembered even after a human picks a
  // different one. Without it the disagreement was only detectable in one direction.
  const derived = useRef(null)
  // What the pin says the address is, when the owner has already written one.
  // null while they agree, or while nothing has been typed (then it just fills).
  const [atPin, setAtPin] = useState(null)   // { name, num } | null
  // The host gates its Continue on this: an unanswered pin question is an unfinished
  // step, not a dismissible hint.
  useEffect(() => { onUnresolved?.(!!atPin) }, [atPin, onUnresolved])
  // Map-first shows the city as CHIPS, and "Other" opens the full list.
  const [cityOpen, setCityOpen] = useState(false)
  // Opens ALREADY OPEN when it has content, so a collapsed section can never hide
  // a filled value from someone reviewing or resuming the form.
  const [entOpen, setEntOpen] = useState(!!value.entrance_apt)
  const [districtOpen, setDistrictOpen] = useState(false)
  // Search arm: the map is not on screen until it is needed - after an answer, when
  // nothing matched, or when the owner asks for it. Sticky once opened.
  // Set when a suggestion is taken, a pin names the address, or the pin's reading is
  // accepted — the three ways this form gets a street it can stand behind.
  const [picked, setPicked] = useState(!!(value.street || '').trim())
  // ⚠️ ASKING TO CHANGE THE ANSWER IS ITS OWN STATE, and it has to be, because the
  // answer has THREE independent sources (`answered = picked || hasPin || missed`).
  // Change cleared `picked` only — and by then `pickStreet` has always written the
  // street's coordinates, so `hasPin` was true and the card simply redrew itself with
  // an empty address line reading "we could not name this spot". The one input this
  // arm has was unreachable: the button did nothing a human could act on (owner
  // report, 2026-08-25, alongside the crash its patch caused in the Desk host).
  // Clearing the pin instead was the wrong fix — the location is still correct, it is
  // the NAME being re-asked, and throwing the pin away would make Change destructive.
  const [editing, setEditing] = useState(false)
  const [mapOpen, setMapOpen] = useState(!!(value.lat != null && value.lng != null))
  // Which suggestion the keyboard is on. -1 = none, and Enter then takes the FIRST —
  // which is what a phone's Go key sends, where there are no arrows at all.
  const [active, setActive] = useState(-1)
  const numberRef = useRef(null)
  // The street text that is already ANSWERED, so the type-ahead does not re-ask it.
  // Picking a suggestion sets `q` to the street's name, which re-fired the search and
  // brought the same street straight back as a one-row list under the input — reading,
  // correctly, as a second street box repeating what the first one now says (owner,
  // 2026-08-08). Cleared the moment a human edits the text again.
  // ⚠️ SEEDED FROM THE INCOMING VALUE, not empty. A street already on the form —
  // a resumed draft ("you left off here"), a property being edited — is an ANSWER,
  // and starting this empty made the block re-ask it on mount: the restored text
  // searched itself and the one-row list reappeared under the input, which is the
  // duplicate box all over again on a path nobody clicks through (owner, 2026-08-08,
  // reporting it a second time from a resumed form).
  const settled = useRef(value.street || '')
  // review 2026-09-23: a street that ARRIVES after mount (the Desk editor prefills an existing
  // property asynchronously) is an answer too. Seeded only at mount, the block kept an empty
  // query and a number edit composed the address from nothing. Adopted only while the box is
  // empty and nobody asked to change it, so it never overwrites what a human is typing.
  useEffect(() => {
    const s = (value.street || '').trim()
    if (!s || q || editing) return
    settled.current = value.street
    setQ(value.street)
    setPicked(true)
  }, [value.street])   // eslint-disable-line react-hooks/exhaustive-deps
  // review 2026-09-23: a number edit. With no known street the stored address is KEPT (the
  // patch omits `address`) — composing it from an empty street saved the bare number ("14")
  // over the property's real address.
  const numberPatch = n => {
    const street = value.street || q
    return street ? { street_number: n, address: [street, n].filter(Boolean).join(' ') } : { street_number: n }
  }

  // Normalised on the way IN as well as out: a draft saved before cityValue existed
  // holds the Georgian "თბილისი", which matches no option in a selector whose values
  // are English — so a resumed form opened reading "All of Georgia" over a Tbilisi
  // address. An unrecognised city is passed through untouched rather than dropped.
  const city = cityValue(value.city, districts) || value.city || DEFAULT_CITY

  // ── street type-ahead ─────────────────────────────────────────────────────
  // Debounced, and every response carries the request number it answered: a slow
  // reply for "ჭავ" must not overwrite the list for "ჭავჭავაძის". Out-of-order
  // responses are the classic type-ahead defect and cost nothing to prevent.
  useEffect(() => {
    if (!resolveStreet) return
    const text = q.trim()
    if (text.length < 2) { setHits(null); return }
    if (text === settled.current) { setHits(null); setBusy(false); return }
    const mine = ++seq.current
    setActive(-1)
    setBusy(true)
    const timer = setTimeout(async () => {
      try {
        const r = await resolveStreet(text, city)
        if (mine !== seq.current) return
        setHits(Array.isArray(r?.candidates) ? r.candidates : [])
      } catch { if (mine === seq.current) setHits([]) }
      finally { if (mine === seq.current) setBusy(false) }
    }, 250)
    return () => clearTimeout(timer)
  }, [q, city, resolveStreet])

  // A pin can ARRIVE with the form — a resumed draft, a property being edited — and
  // until now nothing derived its district, because derivation only ran on a pick or a
  // drag. In the map arm that left the one case where the picker still appears (pin,
  // no district) firing for a form that simply had not been asked. Once, on mount.
  useEffect(() => {
    if (value.lat != null && value.lng != null && !value.district_code) {
      applyPoint(value.lat, value.lng, 'pin')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Resolve a point to a district, then apply it under the override rule.
  // 🔴 THE ANSWER MUST BELONG TO THE PIN THAT ASKED (ADDRESS_SYSTEM_AUDIT #19). This awaited the
  // district lookup and applied whatever came back, with no sequence check — while the nearby
  // reverse-address lookup right below has had one all along. Two pins in quick succession and
  // the OLDER response could land last, leaving coordinates describing the new point and a
  // district describing the old one. The auditor reproduced it with two controlled promises: the
  // new pin wrote NEW, then the old request resolved and wrote OLD.
  // ⚠️ Its OWN counter, not `pinSeq`: applyPoint is CALLED BY findHouse and the drag handler,
  // both of which already own pinSeq, so sharing it would have each of them invalidate the
  // district request they had just made.
  async function applyPoint(lat, lng, from) {
    if (!districtForPoint || lat == null || lng == null) return
    const mine = ++districtSeq.current
    let g = null
    try { g = await districtForPoint(lat, lng) } catch { /* the pin still stands */ }
    if (mine !== districtSeq.current) return    // a later pin already asked; its answer wins
    if (!g?.district_code) return
    if (overridden.current && value.district_code && value.district_code !== g.district_code) {
      // Do NOT resolve it. Show it, and let the human say which one is true —
      // that decision is what declared-vs-geo exists to record.
      derived.current = g.district_code
      setConflict({ code: g.district_code })
      return
    }
    derived.current = g.district_code
    setConflict(null)
    setAutoFrom(from)
    onChange({ district_code: g.district_code })
  }

  // ── choosing a suggestion without the mouse ─────────────────────────────────
  // Owner, 2026-08-09: "somehow user must be able to fill fast by pressing shift here
  // or down arrow and then write in number."
  //
  // ↓/↑ move, Enter or Tab accept, Esc closes. Enter with nothing highlighted takes
  // the first row — which is the case that matters most, because a PHONE keyboard has
  // no arrow keys and its Go key sends exactly that.
  //
  // ⚠️ Tab only accepts while the list is open with something in it; otherwise it must
  // stay Tab, or the address block becomes a keyboard trap for anyone tabbing past it.
  function onKeyDown(e) {
    const list = hits || []
    if (!list.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, list.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, -1)) }
    else if (e.key === 'Escape') { e.preventDefault(); setHits(null); setActive(-1) }
    else if (e.key === 'Enter' || e.key === 'Tab') {
      const s = list[active >= 0 ? active : 0]
      if (!s) return
      e.preventDefault()
      pickStreet(s)
    }
  }

  function pickStreet(s) {
    invalidateHouse()   // #20: a different street answers any house lookup still running
    setAtPin(null)
    setPicked(true)                  // a new street answers any older pin reading
    setEditing(false)                // …and answers the re-ask that Change opened
    const typed = q                    // read BEFORE setQ replaces it with the street
    setQ(s.name_ka)
    setHits(null)
    setBusy(false)
    settled.current = s.name_ka
    // ⚠️ `s.city` is GEORGIAN and the city selector's values are ENGLISH — see
    // cityValue's header. Passing it through set a `<select>` value matching no
    // option, so picking a Tbilisi street made the scope read "All of Georgia"
    // while the tree below still showed Tbilisi. An unrecognised city leaves the
    // scope alone rather than clearing it.
    const cv = cityValue(s.city, districts)
    // The number the owner typed INTO the search box — "ანა პოლიტკოვსკაიას ქუჩა 46".
    // Owner, 2026-08-08: "why it dont pick number from search?"
    const typedNum = houseNumberFromQuery(typed, s.name_ka)
    // ⚠️ A number belongs to the street it was given for. Picking a DIFFERENT street
    // must not carry the old one across: the owner watched "12 კორპ. I", read off a
    // pin on ელიზბარ მინდელის ქუჩა, reappear under ანა პოლიტკოვსკაიას ქუჩა —
    // "this was leftover from previous street". Same street: keep what they typed.
    const sameStreet = (value.street || '') === s.name_ka
    const num = typedNum || (sameStreet ? value.street_number : undefined) || undefined
    onChange({
      // ⚠️ THE STREET'S ID, not only its name (T35). Until now the pick emitted the NAME and
      // threw the identity away, so a correction an agent made by hand survived as free text
      // that the nightly relink might or might not re-match — and could silently overwrite.
      // The gazetteer row is right here at the moment the human chose it; nothing downstream
      // can re-derive that choice as reliably as carrying it.
      // ⚠️ Null, never omitted, when a candidate somehow has none: a caller has to be able to
      // tell "this street has no id" from "this patch is not about the street".
      street_id: s.id ?? null,
      street: s.name_ka, lat: s.lat, lng: s.lng, street_number: num,
      // The typed address stays the record of what the human said; the picked
      // street just makes it canonical.
      address: [s.name_ka, num].filter(Boolean).join(' '),
      ...(cv ? { city: cv } : {}),
    })
    applyPoint(s.lat, s.lng, 'address')
    // Street coordinate first so the map lands immediately; the building refines it.
    if (num) findHouse(s.name_ka, num, s.lat, s.lng)
    setActive(-1)
    // The number is the next thing anyone types. In the search arm that is a separate
    // field, so hand it the focus; in the map arm the one box keeps it and the number
    // is simply typed on the end (see onMapQuery).
    if (layout !== 'map') setTimeout(() => numberRef.current?.focus(), 0)
  }

  // Typing a number is a stream of keystrokes and each one would be an Overpass
  // query, so the lookup waits for the typing to stop. The ANCHOR is the street's
  // coordinate we already have — without a street there is nothing to anchor to and
  // nothing is asked.
  const houseTimer = useRef(null)
  // 🔴 ONE PLACE THAT SAYS "WHATEVER THE HOUSE LOOKUP IS DOING, IT IS NOW WRONG"
  // (ADDRESS_SYSTEM_AUDIT #20). `findHouse` guards its response with `pinSeq`, but that counter
  // only ever moved when a lookup STARTED — never when a change made a running one obsolete.
  // `pickStreet` did not touch it and did not cancel the pending debounce either, so: start
  // resolving street A / 50, choose street B with no number, and A's response still passed its
  // own check and moved B's pin to A/50. The auditor reproduced exactly that — the new street
  // name stayed while the coordinates became the old street's.
  // ⚠️ It cancels BOTH a queued timer and an in-flight request. Cancelling only the timer (which
  // queueHouse already did) leaves the request that is already on the wire, which is the one
  // that does the damage.
  function invalidateHouse() {
    clearTimeout(houseTimer.current)
    houseTimer.current = null
    pinSeq.current++          // any response created before this point now fails its check
  }
  // ⚠️ An EMPTY number cancels rather than queues, and that lives HERE rather than at each
  // caller: the number field is written twice (both layout arms) and the map arm derives it
  // from the typed text, so a rule stated at the call sites is a rule with a site that misses
  // it — which is precisely what the first cut of this fix did.
  function queueHouse(number, streetArg) {
    invalidateHouse()         // a newly typed number answers the previous one
    if (!number) return       // nothing to look up; the cancel above is the whole job
    const street = streetArg || value.street || settled.current
    if (!street || value.lat == null) return
    const { lat, lng } = value
    houseTimer.current = setTimeout(() => findHouse(street, number, lat, lng), 600)
  }

  // ── typing in the map arm's one box ─────────────────────────────────────────
  // Owner, 2026-08-09: "putting number in box after street name must select it."
  // It did not. The box is a QUERY here (that fix stopped it storing "ქუჩა 46" as a
  // street name), and a query is all it stayed — so a number typed after an
  // already-chosen street sat on screen doing nothing, next to an empty number field.
  //
  // Both facts are true at once and the box has to serve both: while the text still
  // BEGINS with the street that was chosen, the rest of it is a house number and is
  // treated as one — applied, and used to move the pin onto the building. Edit the
  // street part and it goes back to being a search.
  //
  // ⚠️ The typed text counts as SETTLED while it is street+number, or the type-ahead
  // would reopen its list over the map on every digit — the same defect, one layer up.
  function onMapQuery(text) {
    setQ(text)
    const street = value.street || ''
    const isStreetPlusNumber = street && text.startsWith(street)
    if (!isStreetPlusNumber) { settled.current = ''; return }
    settled.current = text
    setHits(null)
    const n = houseNumberFromQuery(text, street)
    if (n === value.street_number) return
    setAtPin(null)          // the address just changed; the old comparison is stale
    onChange({ street_number: n || undefined, address: [street, n].filter(Boolean).join(' ') })
    queueHouse(n)   // #20: an emptied number CANCELS in there; it must not be left running
  }

  // ── street + number → the actual building ───────────────────────────────────
  // Owner, 2026-08-09: "even after I write it in its place it dont move pin."
  // It moves now. This is NOT the forward geocoding that was measured impossible —
  // that was Nominatim GUESSING which place a string means. This asks OSM's data
  // which building near the street's own coordinate is tagged with that number, a
  // question with a factual answer. Anchored and street-filtered server-side.
  //
  // ⚠️ Refines the pin, never invents one: no anchor, no move. And it never touches
  // a district — the pin moving re-derives that through the one definition, exactly
  // as a dragged pin does.
  async function findHouse(street, number, anchorLat, anchorLng) {
    if (!houseForAddress || !street || !number || anchorLat == null) return
    const mine = ++pinSeq.current
    let h = null
    try { h = await houseForAddress(street, number, anchorLat, anchorLng) } catch { /* street pin stands */ }
    if (mine !== pinSeq.current || !h?.found) return
    // ⚠️ The pin has just been moved ONTO the typed address, so any earlier reading
    // from an earlier pin is answered — by construction, not by opinion. It used to
    // stay on screen contradicting an address it no longer disagreed with (owner,
    // 2026-08-09: "pin moved but conflict message left open, that's wrong, it must
    // have cleared as pin and street match now").
    setAtPin(null)
    onChange({ lat: h.lat, lng: h.lng })
    applyPoint(h.lat, h.lng, 'address')
  }

  // ── the pin can now NAME the building it sits on ────────────────────────────
  // Measured 2026-08-08 against 10 real property pins: OSM named the street on
  // 10 and the house number on 8. The reverse direction only — a typed address
  // cannot be turned into a building pin for Georgian addresses at all (the same
  // service answers "ილია ჭავჭავაძის გამზირი 60ბ, თბილისი" with a building in
  // Tskhinvali, and that string is one it produced itself moments earlier).
  //
  // ⚠️ It fills, it never overwrites. An empty street field is filled outright;
  // a street the owner already typed or picked is left exactly as it is and the
  // pin's reading is OFFERED instead — same rule, and the same shape, as the
  // district conflict below. Silently rewriting an address someone typed because
  // their finger landed a house along is not a feature.
  async function namePoint(lat, lng) {
    if (!addressForPoint || lat == null || lng == null) return
    const mine = ++pinSeq.current
    let a = null
    try { a = await addressForPoint(lat, lng) } catch { /* the pin still stands */ }
    if (mine !== pinSeq.current) return          // a later drag already won
    // Prefer OUR gazetteer's spelling when the road matched a row we hold, so the
    // form writes the same street string every other surface uses.
    const name = a?.street?.name_ka || a?.road || null
    if (!name) { setAtPin(null); return }
    const num = a.house_number || null
    const typed = (q || '').trim()
    if (!typed) {
      setAtPin(null)
      settled.current = name
      setPicked(true)
      setEditing(false)              // the pin named it — that is an answer
      setQ(name)
      setHits(null)
      onChange({
        // ⚠️ A STALE ID IS WORSE THAN NO ID. Only picking a gazetteer row establishes WHICH
        // canonical street this is; a name read off a pin, typed by hand, or cleared does not.
        // Leaving the previous pick's id attached would file the correction against whatever
        // street the agent chose BEFORE — a confident wrong link, which is the one outcome the
        // street work is under standing owner instruction to avoid.
        street_id: null,
        street: name,
        ...(num && !value.street_number ? { street_number: num } : {}),
        address: [name, num || value.street_number].filter(Boolean).join(' '),
      })
      return
    }
    // Already answered: offer, do not take.
    setAtPin(typed === name && (!num || num === value.street_number) ? null : { name, num })
  }

  function pickPin(lat, lng) {
    onChange({ lat, lng })
    applyPoint(lat, lng, 'pin')
    namePoint(lat, lng)
  }

  function acceptAtPin() {
    if (!atPin) return
    setPicked(true)
    setEditing(false)
    settled.current = atPin.name
    setQ(atPin.name)
    setHits(null)
    onChange({
      // ⚠️ A STALE ID IS WORSE THAN NO ID. Only picking a gazetteer row establishes WHICH
      // canonical street this is; a name read off a pin, typed by hand, or cleared does not.
      // Leaving the previous pick's id attached would file the correction against whatever
      // street the agent chose BEFORE — a confident wrong link, which is the one outcome the
      // street work is under standing owner instruction to avoid.
      street_id: null,
      street: atPin.name,
      ...(atPin.num ? { street_number: atPin.num } : {}),
      address: [atPin.name, atPin.num || value.street_number].filter(Boolean).join(' '),
    })
    setAtPin(null)
  }

  const districtRow = districts.find(d => d.code === value.district_code) || null
  // `value.city` is the SELECTOR's value, which is English ('Tbilisi') — see cityValue.
  // Printing it raw put a Latin word in the middle of an all-Georgian sentence. The
  // stored value stays English; only what the reader sees is translated.
  const cityShown = citiesOf(districts, lang).find(c => (c.city || c.name) === city)?.name || city
  // ⚠️ NON-BREAKING space between the street and its number. Georgian street names
  // are long and the card is narrow, so a plain space let the number wrap onto a line
  // of its own — "ანა პოლიტკოვსკაიას ქუჩა" and then a solitary "46" underneath, which
  // reads as two facts rather than one address (owner, 2026-08-09). The number now
  // wraps WITH the word it belongs to, or not at all.
  // ⚠️ Defined HERE, above both returns: the map arm returns before the search arm's
  // derivations, so a const declared down there is a TDZ crash for map-first that no
  // build step can see.
  const addressLine = [value.street, value.street_number].filter(Boolean).join('\u00A0')
  const mapFirst = layout === 'map'
  const hasPin = value.lat != null && value.lng != null

  const suggestions = (
    <>
      {busy && <p className="text-xs text-muted-foreground">{L.searching}</p>}
      {hits && hits.length > 0 && (
        <div style={{ maxHeight: '16rem' }}
          className="overflow-y-auto overflow-x-hidden rounded-xl border border-border bg-background">
          {hits.map((s, i) => (
            <button key={`${s.name_ka}-${i}`} type="button" onClick={() => pickStreet(s)}
              onMouseEnter={() => setActive(i)} aria-selected={i === active}
              className={`flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm hover:bg-accent ${i === active ? 'bg-accent' : ''}`}>
              <span className="min-w-0 flex-1 truncate text-foreground">{s.name_ka}</span>
              {/* the hint, NOT the identity — see the header on street.district. area_name_ka is the area the
                  street's own location falls in (core 1266): six same-named Tbilisi streets all read "თბილისი"
                  before it, and picking the first put a Vake flat's pin in Lilo. */}
              {(s.area_name_ka || s.district || s.city) && (
                <span className="shrink-0 text-xs text-muted-foreground">{s.area_name_ka || s.district || s.city}</span>
              )}
            </button>
          ))}
          {/* Map-first promises it "works identically for a street we have never
              heard of", so the way out of the list has to be IN the list. */}
          {mapFirst && <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">{L.or_map}</p>}
        </div>
      )}
      {/* An empty result must never look like a broken box. The gazetteer holds
          3,390 streets and Georgia has far more — a village lane, a landmark
          address or a new development simply is not in it, and the typed text is
          a perfectly good answer. "Not found" here is about OUR dictionary. */}
      {hits && hits.length === 0 && !busy && (
        <p className="text-xs text-muted-foreground">{mapFirst ? L.or_map : L.no_street}</p>
      )}
    </>
  )

  // The pin's reading, offered rather than applied — see namePoint. In the search
  // layout it sits under the map because that is what it describes; in map-first it
  // is folded into the read-back sheet, which is already saying what is at the pin.
  // ⚠️ A DECISION, not a suggestion — and it BLOCKS.
  // Owner, 2026-08-09: "if user selects pin it must ask use it (already does) and
  // cancel, and not allow to continue without settling that question, also hinting
  // user by glowing that part."
  // Two things were wrong. It offered only Use it, so the only way past a reading you
  // disagreed with was to ignore a card that never went away; and the form would
  // happily continue holding an address and a pin that name different buildings —
  // exactly the pair of facts this screen exists to keep together.
  // ⚠️ It is also no longer headed "At this pin". In the map arm the SHEET carries
  // that heading, so the screen showed two cards both saying "At this pin" with
  // different house numbers (owner screenshot: 76 above, 7 below).
  // ⚠️ The pulse and the message live ON THIS CARD, not on the step.
  // Owner, 2026-08-09: "let it glow only this part and write error message over that."
  // The wizard's attention class was wrapping the whole address block, so a refusal
  // outlined the city chips, the map and the sheet — everything except the one control
  // that could answer it — and printed its reason at the top of the screen, far from
  // the two buttons it was talking about.
  const atPinRow = atPin && (
    <div className={`rounded-xl border border-amber-500/50 bg-amber-500/10 px-3 py-2 ${attention}`}>
      {blockedMessage && <p className="pb-1 text-sm font-medium text-destructive">{blockedMessage}</p>}
      <p className="text-[11px] text-muted-foreground">{L.pin_says}</p>
      <p className="truncate text-sm font-medium text-foreground">{[atPin.name, atPin.num].filter(Boolean).join(' ')}</p>
      <div className="mt-2 flex gap-2">
        <button type="button" onClick={acceptAtPin}
          className="rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground hover:bg-accent">
          {L.use_it}
        </button>
        <button type="button" onClick={() => setAtPin(null)}
          className="rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground hover:bg-accent">
          {L.pin_keep}
        </button>
      </div>
      {!blockedMessage && <p className="mt-1 text-[11px] text-muted-foreground">{L.pin_decide}</p>}
    </div>
  )

  // City, district, the provenance note and the disagreement card. Identical in both
  // layouts on purpose: the DIRECTION being tested is how the address is captured, and
  // if the district behaved differently in each arm the experiment would be measuring
  // two changes at once and able to attribute neither.
  //
  // No heading of its own: DistrictPicker labels its two controls "City" and "District"
  // separately, and a third label above both was what said Tbilisi is a district.
  const districtSection = (
    <div className="text-xs text-muted-foreground">
      {autoFrom && !overridden.current && districtRow && (
        <p className="text-[11px] text-muted-foreground/80">
          {districtLabel(districtRow, lang)} · {autoFrom === 'pin' ? L.auto_pin : L.auto}
        </p>
      )}
      {conflict && (
        <div className="my-1 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2 py-1.5">
          <p className="text-[11px] text-foreground">{L.conflict}</p>
          <div className="mt-1 flex gap-2">
            <button type="button" className="rounded border border-border px-2 py-0.5 text-[11px]"
              onClick={() => setConflict(null)}>{L.keep_mine}</button>
            <button type="button" className="rounded border border-border px-2 py-0.5 text-[11px]"
              onClick={() => { onChange({ district_code: conflict.code }); overridden.current = false; setConflict(null); setAutoFrom('pin') }}>
              {L.use_pin}
            </button>
          </div>
        </div>
      )}
      <DistrictPicker districts={districts} lang={lang} mode="single"
        value={value.district_code || null}
        onChange={code => {
          overridden.current = true
          setAutoFrom(null)
          // ⚠️ Owner, 2026-08-09: "this district selector allow me to change district
          // thats stupid — leaves street name and changing district." It did: picking
          // Didube under an address in Saburtalo replaced the answer in silence, and
          // the form then held two facts that cannot both be true with nothing said.
          // The disagreement card already existed but only fired when the PIN moved
          // onto a hand-picked district — the other direction went unnoticed. Same
          // card, same choice, both directions now.
          setConflict(derived.current && code && code !== derived.current ? { code: derived.current } : null)
          onChange({ district_code: code || undefined })
        }}
        city={city}
        onCityChange={c => onChange({ city: c || undefined })}
        labels={labels} />
    </div>
  )

  // "My location" from the arrival screen: one tap, not open-the-map-then-find-the
  // button. Failure is silent and leaves the map open - a refused permission is not an
  // error the owner needs explained, and the map is still there to tap.
  function useMyLocation() {
    setMapOpen(true)
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      pos => pickPin(pos.coords.latitude, pos.coords.longitude),
      () => {}, { enableHighAccuracy: true, timeout: 8000 })
  }

  // ── entrance / apartment ────────────────────────────────────────────────────
  // Owner, 2026-08-09, on the designer's "+ სადარბაზო / ბინა": "thats great idea."
  //
  // ⚠️ ITS OWN FIELD, AND NEVER PART OF `address`. That is the entire safety behind
  // the promise printed under it. A repost publishes `address` to myhome / ss / korter
  // (core_reshare_payload maps location_text → address), so folding a flat number into
  // the address string — the natural way to write an address — would post the owner's
  // door number to three public portals. It travels as its own key, lands in
  // `listing.meta` through stamp_listing_meta, and NEITHER the share page nor the
  // repost payload reads `meta`: both use named field lists (verified 2026-08-09).
  const entranceBlock = (
    <div>
      {!entOpen ? (
        <button type="button" onClick={() => setEntOpen(true)}
          className="text-[13px] font-medium text-muted-foreground underline underline-offset-[3px]">
          {L.entrance_add}
        </button>
      ) : (
        <label className="block text-xs text-muted-foreground">{L.entrance_label}
          <input {...NO_AUTOFILL} className={BOX} value={value.entrance_apt || ''}
            onChange={e => onChange({ entrance_apt: e.target.value })} />
          <span className="mt-1 block text-[11px] text-muted-foreground">{L.entrance_note}</span>
        </label>
      )}
    </div>
  )

  // ── city, as chips, in the map arm ──────────────────────────────────────────
  // Owner, 2026-08-09: "we may still need city selector like this — so osm only looks
  // in that street." Correct, and it is a FUNCTIONAL need rather than a preference:
  // the street search is city-scoped and defaults to Tbilisi, so removing the picker
  // left an owner in Kutaisi searching Tbilisi's streets with no way to say otherwise.
  // The district went because the pin answers it; the city stays because it is a
  // QUESTION THE PIN CANNOT ANSWER YET — it scopes the search that happens first.
  //
  // Chips, not the dropdown: three cities cover the market, and the shape is the one
  // the client form already uses for exactly this question.
  const cityChips = (() => {
    const all = citiesOf(districts, lang)
    // Owner's three, 2026-08-09. NOT the gazetteer's biggest — Kutaisi holds 281
    // streets to Rustavi's 129 — but the owner picks the market, and everything
    // else stays one tap away behind Other.
    const quick = ['Tbilisi', 'Rustavi', 'Batumi']
      .map(c => all.find(x => (x.city || x.name) === c)).filter(Boolean)
    const isQuick = quick.some(c => (c.city || c.name) === city)
    return (
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">{L.city}</p>
        <div className="flex flex-wrap gap-2">
          {quick.map(c => {
            const v = c.city || c.name
            return (
              <button key={c.code} type="button" onClick={() => { setCityOpen(false); onChange({ city: v }) }}
                className={`rounded-full border px-3 py-1.5 text-sm ${city === v
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-foreground'}`}>
                {c.name}
              </button>
            )
          })}
          {/* "Other" stays visibly SELECTED while a city outside the three is chosen,
              or the owner who picked Zugdidi sees three unselected chips and no trace
              of their own answer. */}
          <button type="button" onClick={() => setCityOpen(o => !o)}
            className={`rounded-full border px-3 py-1.5 text-sm ${!isQuick
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-background text-foreground'}`}>
            {!isQuick ? (all.find(x => (x.city || x.name) === city)?.name || L.other) : L.other}
          </button>
        </div>
        {cityOpen && (
          <select className={BOX} value={city || ''}
            onChange={e => { onChange({ city: e.target.value || undefined }); setCityOpen(false) }}>
            <option value="">{L.allCities}</option>
            {all.map(c => <option key={c.code} value={c.city || c.name}>{c.name}</option>)}
          </select>
        )}
      </div>
    )
  })()

  // ── MAP-FIRST ───────────────────────────────────────────────────────────────
  // The map owns the screen and search is a shortcut into it. The read-back sheet
  // below it is the whole argument for this direction: the pin does not just record
  // a location, it NAMES the building — which only works because the reverse lookup
  // does (10/10 streets, 8/10 house numbers on real pins).
  //
  // ⚠️ A SEARCH PICK NEVER CLAIMS A HOUSE NUMBER, and this is the one correctness
  // rule the direction turns on. Our gazetteer holds ONE coordinate per street, so
  // choosing a suggestion drops the pin at that street's stored point — house 24 of
  // ანა პოლიტკოვსკაიას ქუჩა, whatever the owner meant. Reading a number back there
  // would invent an address, which is the failure this whole redesign exists to end.
  // So `namePoint` runs on a PIN move only, and after a search pick the number box
  // shows what the owner typed, which is usually nothing, and asks for it.
  if (mapFirst) {
    return (
      <div className="space-y-3">
        {cityChips}
        <div className="relative">
          {renderMap && renderMap({ lat: value.lat ?? null, lng: value.lng ?? null, onPick: pickPin, layout })}
          {/* ⚠️ z-1200, not 500. Leaflet puts its own controls at z-index 1000, so the
              zoom + and − painted straight through the suggestion list — the owner saw
              two buttons floating over the street names (2026-08-09). Anything laid
              over this map has to clear 1000.
              ⚠️ INLINE, not `z-[1200]`. An arbitrary-value class only exists if the
              content scanner saw that exact string, which already made the map render
              at 0px once. Stacking order is load-bearing here; it does not get to
              depend on which directory the build ran from. */}
          <div className="absolute inset-x-3 top-3" style={{ zIndex: 1200 }}>
            {/* ⚠️ HERE THE BOX IS A QUERY, NOT THE ADDRESS. In the search arm this same
                input IS the street field, so every keystroke writes `street`. Doing that
                here was wrong twice over: the map arm has ONE box, so an owner typing
                their address naturally types the number into it too — and `street`
                really did end up as "ანა პოლიტკოვსკაიას ქუჩა 46", which the read-back
                sheet then printed with the house number a second time (owner screenshot,
                2026-08-08). It also meant the typed text never equalled `settled`, so
                the suggestion list stayed open over the map forever.
                In map-first the address is written by a SUGGESTION PICK or by the PIN.
                Searching is how you move the map, and nothing else. */}
            <input {...NO_AUTOFILL} className={`${BOX} shadow-sm`} value={q} placeholder={L.map_search_ph}
              onChange={e => onMapQuery(e.target.value)} onKeyDown={onKeyDown} />
            <div className="mt-1">{suggestions}</div>
          </div>
        </div>

        {hasPin && (
          <div className="space-y-2 rounded-xl border border-border bg-card p-3">
            <p className="text-[11px] text-muted-foreground">{L.at_pin}</p>
            {/* Street known but unnamed is a real, common state — 2 of 10 real pins.
                Say what we have, and never dress "" up as an answer. */}
            <p className="text-[17px] font-semibold leading-6 text-foreground">
              {addressLine || L.unnamed}
            </p>
            <div className="flex items-center gap-2 border-t border-border/60 pt-2">
              <span className="min-w-0 flex-1 text-[13px] text-muted-foreground">{L.other_number}</span>
              <input {...NO_AUTOFILL} className="w-20 shrink-0 rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
                value={value.street_number || ''} inputMode="text"
                onChange={e => { setAtPin(null); onChange(numberPatch(e.target.value))
                  queueHouse(e.target.value) }} />
            </div>

            {/* ⚠️ The district is its OWN row, outside the "at this pin" claim above.
                It briefly sat inside it, and a hand-picked district then rendered as
                though the pin had reported it — the card read "At this pin · Krtsanisi"
                over an address in Saburtalo, because the owner had chosen Krtsanisi
                themselves (owner screenshot, 2026-08-08). A card that says "at this pin"
                may only contain what is at the pin. */}
            {(districtRow || value.city) && (
              <div className="flex items-center gap-2 border-t border-border/60 pt-2">
                <span className="min-w-0 flex-1 text-[13px] text-foreground">
                  {[districtRow && districtLabel(districtRow, lang), cityShown].filter(Boolean).join(' · ')}
                  {autoFrom && !overridden.current && (
                    <span className="text-muted-foreground"> · {autoFrom === 'pin' ? L.auto_pin : L.auto}</span>
                  )}
                </span>

              </div>
            )}
          </div>
        )}

        {atPinRow}
        {entranceBlock}
        {/* ── THE MAP ARM DOES NOT ASK FOR A DISTRICT ────────────────────────────
            Owner, 2026-08-09: "in this version do we need district selector? I guess
            no." No — and the evidence is stronger than the instinct. The one genuine
            owner submission this form has ever received was RUINED by this control:
            a Tskneti address filed under BAT_AIRPORT, the Airport district of Batumi,
            because a 236-row alphabetical list made the wrong answer the easy one.
            Where the pin can answer, asking a person is not neutral — it is an
            opportunity to be wrong about a fact we already know.

            So here the district is a RESULT, printed in the sheet, not a question.
            The picker appears in exactly one case: we have a pin and still could not
            place it (outside every polygon we hold). Location is mandatory, so a form
            that cannot derive a district and cannot ask for one is a dead end.

            ⚠️ This is also what makes the 0516 arms genuinely different. The search
            arm keeps the picker; this one removes it. If both asked the same question
            the experiment would compare two spellings of one design. */}
        {hasPin && !value.district_code && districtSection}
      </div>
    )
  }

  // ── SEARCH-FIRST (1b) ───────────────────────────────────────────────────────
  // One input on arrival; street, pin, district and city are RESULTS, shown on one
  // card the owner confirms. Built to the designer's frames (2026-08-09) — until now
  // this arm was the ORIGINAL five-control screen with fixes on top, so the 0516
  // comparison was a design against a patched legacy screen, which measures the
  // patching and nothing else.
  //
  // ⚠️ The map is absent at the start, and that IS the direction: this arm bets that
  // an owner would rather type their address than find their roof. It appears when
  // there is something to show (an answer), when there is nothing to type (the
  // dictionary missed), or when the owner asks for it. The map arm makes the opposite
  // bet on the same screen — that is the whole experiment, and if both showed a map
  // on arrival there would be nothing to compare.
  // ⚠️ ANSWERED means a street was CHOSEN, not that a character was typed. Keying it
  // on the text collapsed the arrival into the result card on the first keystroke, so
  // the suggestion list never got a chance to appear and nothing ever resolved: the
  // card showed the raw typed string with no district, no city and no pin (caught by
  // rendering it, 2026-08-09). A dictionary miss also counts — the typed text IS the
  // answer then, which is the state frame 5 of the design draws.
  const missed = !!(hits && hits.length === 0 && !busy && q.trim().length >= 2)
  // `editing` wins over all three: it is the human saying "the answer you are showing
  // me is not the one I want", which no derived signal can represent. See its useState.
  const answered = !editing && (picked || hasPin || missed)

  const showMap = mapOpen || hasPin || (hits && hits.length === 0 && !busy)

  const fields = (
    <div className="space-y-3">
      {!answered ? (
        <>
          <label className="block text-xs text-muted-foreground">{L.street}
            <input {...NO_AUTOFILL} className={BOX} value={q} placeholder={L.street_ph} onKeyDown={onKeyDown}
              onChange={e => { settled.current = ''; setQ(e.target.value); onChange({ street_id: null, street: e.target.value, address: [e.target.value, value.street_number].filter(Boolean).join(' ') }) }} />
          </label>
          {suggestions}
          {!q.trim() && (
            <>
              <div className="flex items-center gap-3 py-1">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">{L.or}</span>
                <span className="h-px flex-1 bg-border" />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setMapOpen(true)}
                  className="flex-1 rounded-xl border border-border bg-card px-3 py-3 text-sm font-medium text-foreground hover:bg-accent/40">
                  {L.mark_on_map}
                </button>
                <button type="button" onClick={useMyLocation}
                  className="flex-1 rounded-xl border border-border bg-card px-3 py-3 text-sm font-medium text-foreground hover:bg-accent/40">
                  {L.my_location}
                </button>
              </div>
              <p className="text-xs leading-5 text-muted-foreground">{L.not_listed_hint}</p>
            </>
          )}
        </>
      ) : (
        <div className="space-y-3 rounded-xl border border-border bg-card p-3">
          {/* The answer, and one way back to editing it. Change returns to the SEARCH
              rather than opening a second field — this arm has exactly one input, which
              is the point of it. */}
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[17px] font-semibold leading-6 text-foreground">
                {addressLine || L.unnamed}
              </p>
              {cityShown && <p className="text-xs text-muted-foreground">{cityShown}</p>}
            </div>
            <button type="button"
              // ⚠️ `''`, NOT `undefined`. This patch crosses into whatever state the HOST
              // keeps, and the Desk's `prop` holds these three as strings it calls `.trim()`
              // on unguarded — so `undefined` crashed the whole tab (owner, 2026-08-25).
              // The fill form survived only because it happens to read `(form.address||'')`.
              // A clear is "" here; a field this control does not own is simply absent from
              // the patch. Never send `undefined` to say "empty" — it is indistinguishable
              // from "not mentioned" to a spread, and it destroys the receiver's type.
              onClick={() => { settled.current = ''; setPicked(false); setEditing(true); setQ(''); setAtPin(null); setHits(null); onChange({ street_id: null, street: '', street_number: '', address: '' }) }}
              className="shrink-0 text-[13px] font-medium text-muted-foreground underline underline-offset-[3px]">
              {L.change}
            </button>
          </div>

          {/* ⚠️ A CORRECTION, not a second question. The heading above already shows the
              number as part of the address, so a box labelled plainly "No." underneath it
              reads as being asked the same thing twice (owner, 2026-08-09: "do we need
              number twice?"). Same framing and the same row shape as the map arm, so one
              is the answer and the other is visibly the way to fix it.
              It stays even when there is no number yet: a street picked without one has
              to be completable without wiping the street to retype it. */}
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 text-[13px] text-muted-foreground">{L.other_number}</span>
            <input {...NO_AUTOFILL} ref={numberRef} value={value.street_number || ''} inputMode="text"
              className="w-20 shrink-0 rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
              onChange={e => { setAtPin(null); onChange(numberPatch(e.target.value)); queueHouse(e.target.value) }} />
          </div>

          {!mapBeside && renderMap && renderMap({ lat: value.lat ?? null, lng: value.lng ?? null, onPick: pickPin, layout })}
          {atPinRow}

          {/* District and city as CONFIRMABLE ROWS, not questions — the same conclusion
              the map arm reached, arrived at from the other side. The picker still opens
              from Change, and opens itself when nothing could be derived or when a human
              and the pin disagree. */}
          <div className="flex items-center gap-2 border-t border-border/60 pt-2">
            <span className="min-w-0 flex-1 text-[13px] text-foreground">
              {districtRow ? districtLabel(districtRow, lang) : L.district}
              {autoFrom && !overridden.current && districtRow && (
                <span className="text-muted-foreground"> · {autoFrom === 'pin' ? L.auto_pin : L.auto}</span>
              )}
            </span>
            <button type="button" onClick={() => setDistrictOpen(o => !o)}
              className="shrink-0 rounded-lg border border-border bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-accent">
              {L.change}
            </button>
          </div>
          {(districtOpen || !value.district_code || conflict) && districtSection}

          {entranceBlock}
        </div>
      )}

      {/* Asked for, or forced by a dictionary miss, before there is any answer. */}
      {!mapBeside && !answered && showMap && renderMap &&
        renderMap({ lat: value.lat ?? null, lng: value.lng ?? null, onPick: pickPin, layout })}
    </div>
  )
  if (!mapBeside || !renderMap || !(answered || showMap)) return fields
  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-4 space-y-3 lg:space-y-0" data-address-map-beside>
      {fields}
      <div className="lg:sticky lg:top-24 self-start">
        {renderMap({ lat: value.lat ?? null, lng: value.lng ?? null, onPick: pickPin, layout })}
      </div>
    </div>
  )
}
